/**
 * global-swiper.js
 * -----------------------------------------------------------
 * Generic lazy-initialized Swiper + lazy video loader
 *
 * Usage example from a page:
 *
 * if (window.initLazyVideoSwiperGroup) {
 *   window.initLazyVideoSwiperGroup({
 *     groupName: "setchair",
 *     debug: true,
 *
 *     sliderConfigs: [
 *       {
 *         "swiper-container-id": "collection-setchair-adela",
 *         "group-wrapper-id": "#chair-adela",
 *         loop: true,
 *         pagination: ".adela-control .collection-setchair-pagination",
 *         next: ".adela-control .collection-setchair-products-gallery-next",
 *         prev: ".adela-control .collection-setchair-products-gallery-prev"
 *       },
 *       // ...
 *     ],
 *
 *     baseSwiperOptions: {
 *       slidesPerView: 1.3,
 *       spaceBetween: 15,
 *       centeredSlides: true,
 *       centeredSlidesBounds: true,
 *       initialSlide: 1,
 *       lazy: true,
 *       autoplay: false,
 *       breakpoints: {
 *         900: { slidesPerView: "auto" }
 *       }
 *     }
 *   });
 * }
 *
 * -----------------------------------------------------------
 * Features:
 * - Per-group config (groupName)
 * - Per-group debug flag (debug: true/false)
 * - Per-slider loop flag (loop: true/false)
 * - Lazy init via IntersectionObserver (300px before viewport)
 * - Lazy video loading via <source data-src="...">
 * - Video play/pause on slide change
 * - Swiper v11 compatible
 * - Safe pagination / navigation (no crash if DOM missing)
 * -----------------------------------------------------------
 */

(function () {
  "use strict";

  // Namespace on window for debugging
  var GLOBAL_NS = window.__GLOBAL_SLIDER__ || {};
  window.__GLOBAL_SLIDER__ = GLOBAL_NS;

  // Store Swiper instances and group configs for debugging
  GLOBAL_NS.instances = GLOBAL_NS.instances || {};
  GLOBAL_NS.groups = GLOBAL_NS.groups || {};

  /**
   * Small helper: safe log wrapper used inside groups.
   * Every group will create its own logger that prefixes messages.
   */
  function createGroupLogger(groupName, debugFlag) {
    return function log() {
      if (!debugFlag) return;
      var args = Array.prototype.slice.call(arguments);
      // Prefix everything for easy filtering
      args.unshift("🐞 [GLOBAL-SLIDER]", "(group:", groupName + ")");
      console.log.apply(console, args);
    };
  }

  /* -----------------------------------------------------------
   * SHARED VIDEO HELPERS
   * --------------------------------------------------------- */

  /**
   * Lazy-load <source data-src="..."> items in a <video>.
   * - Only runs once per video (video.dataset.loaded = "true")
   */
  function loadLazyVideoSources(video, log) {
    if (!video) return;
    if (video.dataset && video.dataset.loaded === "true") {
      if (log) log("ℹ️ Already loaded video sources", video);
      return;
    }

    var sources = video.querySelectorAll("source[data-src]");
    var loaded = false;

    sources.forEach(function (src) {
      if (!src.src && src.dataset && src.dataset.src) {
        src.src = src.dataset.src;
        loaded = true;
      }
    });

    if (loaded) {
      video.load();
      if (video.dataset) {
        video.dataset.loaded = "true";
      }
      if (log) log("🎥 Lazy-loaded video sources", video);
    }
  }

  /**
   * Pause all <video> elements inside a container.
   */
  function pauseVideosInContainer(container, log) {
    if (!container) return;
    var vids = container.querySelectorAll("video");
    vids.forEach(function (v) {
      try {
        if (!v.paused) {
          v.pause();
          if (log) log("⏸ Paused video", v);
        }
      } catch (e) {
        if (log) log("⚠️ Error while pausing video", e);
      }
    });
  }

  /* -----------------------------------------------------------
   * CORE: CREATE A SINGLE SWIPER INSTANCE
   * (Safe pagination/navigation handling)
   * --------------------------------------------------------- */

  function createSwiperInstance(swiperEl, sliderConfig, groupConfig, groupLog) {
    var baseSwiperOptions = groupConfig.baseSwiperOptions || {};
    var groupName = groupConfig.groupName || "unknown-group";
    var debug = !!groupConfig.debug;
    var log = groupLog || createGroupLogger(groupName, debug);

    if (!swiperEl) {
      log("❌ Swiper element not found for config:", sliderConfig);
      return null;
    }

    var swiperId = sliderConfig["swiper-container-id"] || swiperEl.id || "(no-id)";
    var loop =
      typeof sliderConfig.loop === "boolean" ? sliderConfig.loop : true;

    // -------------------------------------------------------
    // Pagination (optional, safe)
    // -------------------------------------------------------
    var paginationOptions;
    if (sliderConfig.pagination) {
      var paginationEl = document.querySelector(sliderConfig.pagination);

      if (paginationEl) {
        paginationOptions = {
          el: sliderConfig.pagination,
          clickable: true
        };
        log(
          "✅ Pagination FOUND for swiper:",
          swiperId,
          "| selector:",
          sliderConfig.pagination
        );
      } else {
        log(
          "⚠️ Pagination element NOT FOUND for swiper:",
          swiperId,
          "| selector:",
          sliderConfig.pagination
        );
        // IMPORTANT:
        // Do NOT set any pagination option at all here.
        // Swiper will crash if it thinks pagination is enabled
        // but the internals don't find a proper .el element.
        paginationOptions = undefined;
      }
    }

    // -------------------------------------------------------
    // Navigation (optional, safe)
    // -------------------------------------------------------
    var navigationOptions;
    var hasPrev = !!sliderConfig.prev;
    var hasNext = !!sliderConfig.next;

    if (hasPrev || hasNext) {
      var navConfig = {};
      if (hasPrev) {
        var prevEl = document.querySelector(sliderConfig.prev);
        if (prevEl) {
          navConfig.prevEl = sliderConfig.prev;
        } else {
          log(
            "⚠️ Prev element NOT FOUND for swiper:",
            swiperId,
            "| selector:",
            sliderConfig.prev
          );
        }
      }
      if (hasNext) {
        var nextEl = document.querySelector(sliderConfig.next);
        if (nextEl) {
          navConfig.nextEl = sliderConfig.next;
        } else {
          log(
            "⚠️ Next element NOT FOUND for swiper:",
            swiperId,
            "| selector:",
            sliderConfig.next
          );
        }
      }

      if (navConfig.prevEl || navConfig.nextEl) {
        navigationOptions = navConfig;
        log(
          "✅ Navigation bound for swiper:",
          swiperId,
          "| prev:",
          navConfig.prevEl,
          "| next:",
          navConfig.nextEl
        );
      } else {
        log("⚠️ Navigation elements NOT FOUND for swiper:", swiperId);
      }
    }

    log("🔧 Creating Swiper:", swiperId, "| loop:", loop);

    // Build final Swiper options
    var swiperOptions = Object.assign({}, baseSwiperOptions, {
      loop: loop
    });

    if (paginationOptions) {
      swiperOptions.pagination = paginationOptions;
    }

    if (navigationOptions) {
      swiperOptions.navigation = navigationOptions;
    }

    // -------------------------------------------------------
    // Video handling hooks (init + slideChange)
    // -------------------------------------------------------

    var originalOn = baseSwiperOptions.on || {};
    var groupWrapperSelector = sliderConfig["group-wrapper-id"];
    var groupWrapper =
      groupWrapperSelector
        ? document.querySelector(groupWrapperSelector)
        : null;

    var extraHandlers = {
      init: function () {
        log("✅ Swiper initialized:", swiperId);

        if (!groupWrapper) {
          log(
            "ℹ️ No group wrapper found for swiper:",
            swiperId,
            "| selector:",
            groupWrapperSelector
          );
        }

        // Attempt to play video in initial slide if present
        var activeSlide =
          swiperEl.querySelector(".swiper-slide-active") ||
          swiperEl.querySelector(".swiper-slide");
        if (activeSlide) {
          var initialVideo = activeSlide.querySelector("video");
          if (initialVideo) {
            log(
              "🎞 [GLOBAL-SLIDER] Initial slide has video → load & play:",
              swiperId
            );
            loadLazyVideoSources(initialVideo, log);
            try {
              initialVideo.play().catch(function () {
                log(
                  "⚠️ [GLOBAL-SLIDER] Autoplay blocked on init for:",
                  swiperId
                );
              });
            } catch (e) {
              log("⚠️ Error while playing initial video", e);
            }
          }
        }
      },

      slideChange: function () {
        // "this" is Swiper instance
        var self = this;
        var activeIndex = self.activeIndex;

        // Pause all existing videos in the wrapper
        if (groupWrapper) {
          pauseVideosInContainer(groupWrapper, log);
        }

        var slide = self.slides[activeIndex];
        if (!slide) {
          log("ℹ️ slideChange but no slide at index", activeIndex, "for", swiperId);
          return;
        }

        var video = slide.querySelector("video");
        if (video) {
          log(
            "🎞 [GLOBAL-SLIDER] slideChange has video → load & play:",
            swiperId
          );
          loadLazyVideoSources(video, log);
          try {
            video.play().catch(function () {
              log(
                "⚠️ [GLOBAL-SLIDER] Autoplay blocked on slideChange for:",
                swiperId
              );
            });
          } catch (e) {
            log("⚠️ Error while playing video on slideChange", e);
          }
        } else {
          log("➡ [GLOBAL-SLIDER] slideChange → no video in active slide:", swiperId);
        }
      }
    };

    // Merge user-provided `on` handlers (if any) with our extra handlers
    var mergedOn = Object.assign({}, originalOn, extraHandlers);
    swiperOptions.on = mergedOn;

    // Create Swiper instance
    var instance = new Swiper(swiperEl, swiperOptions);

    return instance;
  }

  /* -----------------------------------------------------------
   * PUBLIC: initLazyVideoSwiperGroup
   * --------------------------------------------------------- */

  /**
   * Initialize a group of sliders with lazy Swiper + video behaviour.
   *
   * @param {Object} groupConfig
   *  - groupName: string (required)
   *  - debug: boolean
   *  - sliderConfigs: Array<{
   *      "swiper-container-id": string (required),
   *      "group-wrapper-id": string (required),
   *      loop?: boolean,
   *      pagination?: string, // CSS selector
   *      next?: string,       // CSS selector
   *      prev?: string        // CSS selector
   *    }>
   *  - baseSwiperOptions: Swiper options shared by the group
   */
  function initLazyVideoSwiperGroup(groupConfig) {
    if (!groupConfig || !groupConfig.groupName) {
      console.warn(
        "⚠️ [GLOBAL-SLIDER] initLazyVideoSwiperGroup called without groupName"
      );
      return;
    }

    var groupName = groupConfig.groupName;
    var debug = !!groupConfig.debug;
    var sliderConfigs = groupConfig.sliderConfigs || [];
    var log = createGroupLogger(groupName, debug);

    GLOBAL_NS.groups[groupName] = groupConfig;
    GLOBAL_NS.instances[groupName] = GLOBAL_NS.instances[groupName] || {};

    log("Debug mode:", debug, "| Group:", groupName);

    if (!sliderConfigs.length) {
      log("ℹ️ No sliderConfigs provided for group:", groupName);
      return;
    }

    // IntersectionObserver: lazy-init each Swiper
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          var targetEl = entry.target;
          var id = targetEl.id;
          var sliderConfig = sliderConfigs.find(function (cfg) {
            return cfg["swiper-container-id"] === id;
          });

          if (!sliderConfig) {
            log(
              "⚠️ Observed slider but no matching config found. Element id:",
              id
            );
            observer.unobserve(targetEl);
            return;
          }

          log(
            "👀 [GLOBAL-SLIDER] Slider entering viewport:",
            id,
            "| group:",
            groupName
          );

          // Avoid duplicate initialization
          var groupInstances = GLOBAL_NS.instances[groupName];
          if (groupInstances[id]) {
            log("ℹ️ Swiper already initialized for:", id);
            observer.unobserve(targetEl);
            return;
          }

          var swiperEl = document.getElementById(id);
          var instance = createSwiperInstance(
            swiperEl,
            sliderConfig,
            groupConfig,
            log
          );

          if (instance) {
            groupInstances[id] = instance;
          }

          observer.unobserve(targetEl);
        });
      },
      {
        root: null,
        rootMargin: "300px 0px",
        threshold: 0
      }
    );

    // Attach observer to each slider element
    sliderConfigs.forEach(function (cfg) {
      var elId = cfg["swiper-container-id"];
      if (!elId) {
        log("⚠️ Missing swiper-container-id in config:", cfg);
        return;
      }

      var el = document.getElementById(elId);
      if (!el) {
        log("❌ Missing slider HTML for id:", elId);
        return;
      }

      log("🔍 [GLOBAL-SLIDER] Observing slider:", elId);
      observer.observe(el);

      // Optional: if it's already in viewport on load and we have
      // a global helper isElementInViewport, we can eager-init it.
      if (
        typeof window.isElementInViewport === "function" &&
        window.isElementInViewport(el)
      ) {
        log("⚡ Already in viewport, eager init:", elId);
        var groupInstances = GLOBAL_NS.instances[groupName];
        if (!groupInstances[elId]) {
          var instance = createSwiperInstance(
            el,
            cfg,
            groupConfig,
            log
          );
          if (instance) {
            groupInstances[elId] = instance;
          }
          observer.unobserve(el);
        }
      }
    });
  }

  // Expose the initializer globally
  window.initLazyVideoSwiperGroup = initLazyVideoSwiperGroup;
})();
